import { useEffect, useRef, useState } from 'react';
import { Alert, Button, Checkbox, FormControlLabel, Stack, TextField, Typography } from '@mui/material';
import { useMutation, useQuery, useQueryClient } from 'react-query';
import { useTranslation } from 'react-i18next';
import { safeHotelImageUrl } from '@bandido/shared/types';
import { client } from '@frontend/features/common/services';
import { catalogAdminKey, useCatalogAdminCapability } from './queries';
import type { EditorialSnapshot, Publication } from './editorial';
import { validPriorityReason } from './priorityOrder';
interface SourcePreview {
  supplier_hotel_id: number;
  source_revision: string;
  source_observed_at: string;
  source_updated_at: string | null;
  snapshot: EditorialSnapshot;
}
export function HotelPreparation({
  hotelId,
  sourceId,
  onBusyChange,
}: {
  hotelId: string;
  sourceId: number;
  onBusyChange?(busy: boolean): void;
}) {
  const { t } = useTranslation();
  const capability = useCatalogAdminCapability();
  const allowed = capability.data?.allowed === true;
  const cache = useQueryClient();
  const path = `hotel-catalog-admin/hotels/${encodeURIComponent(hotelId)}`;
  const publication = useQuery({
    queryKey: [...catalogAdminKey, 'editorial', hotelId, 'publication'],
    queryFn: async () => (await client.get<{ data: Publication }>(`${path}/publication`)).data.data,
    enabled: allowed,
    retry: false,
  });
  const eligibility = useQuery({
    queryKey: [...catalogAdminKey, 'eligibility', hotelId],
    queryFn: async () =>
      (await client.get<{ data: { status: string; active_supplier_links_count: number } }>(`${path}/eligibility`)).data
        .data,
    enabled: allowed,
    retry: false,
  });
  const missing =
    publication.isError && (publication.error as { response?: { status?: number } })?.response?.status === 404;
  const [rights, setRights] = useState(false);
  const [publishReason, setPublishReason] = useState('');
  const [activationReason, setActivationReason] = useState('');
  const [activationConfirmed, setActivationConfirmed] = useState(false);
  const reactivating = eligibility.data?.status === 'archived';
  const canActivate = eligibility.data?.status === 'draft' || reactivating;
  useEffect(() => {
    setActivationConfirmed(false);
  }, [eligibility.data?.status]);
  const inFlight = useRef(false);
  const preview = useMutation({
    mutationFn: async () =>
      (await client.post<{ data: SourcePreview }>(`${path}/source-preview`, { supplier_hotel_id: sourceId })).data.data,
    retry: false,
  });
  const publish = useMutation({
    mutationFn: async () => {
      if (
        !allowed ||
        publication.isFetching ||
        !preview.isSuccess ||
        !missing ||
        !rights ||
        !validPriorityReason(publishReason) ||
        preview.data?.supplier_hotel_id !== sourceId
      ) throw new Error('Review required');
      return client.post(`${path}/first-publication`, {
        supplier_hotel_id: sourceId,
        source_revision: preview.data.source_revision,
        reason: publishReason.trim(),
        confirm_global_publication: true,
      });
    },
    retry: false,
    onSuccess: async () => {
      preview.reset();
      setRights(false);
      await Promise.all([
        cache.invalidateQueries([...catalogAdminKey, 'editorial', hotelId]),
        cache.invalidateQueries([...catalogAdminKey, 'detail', hotelId]),
        cache.invalidateQueries([...catalogAdminKey, 'inventory']),
      ]);
    },
    onSettled: () => {
      inFlight.current = false;
    },
  });
  const activate = useMutation({
    mutationFn: async () => {
      if (
        !allowed ||
        eligibility.isError ||
        eligibility.isFetching ||
        !activationConfirmed ||
        !validPriorityReason(activationReason) ||
        !canActivate
      ) throw new Error('Activation review required');
      return client.post(`${path}/activate`, { reason: activationReason.trim() });
    },
    retry: false,
    onSuccess: async () => {
      setActivationConfirmed(false);
      await Promise.all([
        eligibility.refetch(),
        cache.invalidateQueries([...catalogAdminKey, 'detail', hotelId]),
        cache.invalidateQueries([...catalogAdminKey, 'inventory']),
      ]);
    },
    onSettled: () => {
      inFlight.current = false;
    },
  });
  const pending = preview.isLoading || publish.isLoading || activate.isLoading;
  const uncertain = publish.isError || activate.isError;
  const locked = pending || uncertain;
  useEffect(() => {
    onBusyChange?.(locked);
    return () => onBusyChange?.(false);
  }, [locked, onBusyChange]);
  if (!allowed) return null;
  return (
    <Stack spacing={2}>
      <Typography variant="h5">{t('hotelPreparation.title')}</Typography>
      <Alert severity="info">{t('hotelPreparation.scope')}</Alert>
      <Typography variant="h6">{t('hotelPreparation.publication')}</Typography>
      {publication.isFetching ? (
        <Typography>{t('hotelCatalogAdmin.loading')}</Typography>
      ) : publication.data && !publication.isError ? (
        <Alert severity={publication.data.published ? 'success' : 'warning'}>
          {t(publication.data.published ? 'hotelPreparation.published' : 'hotelPreparation.withdrawn')}
        </Alert>
      ) : missing ? (
        <Typography>{t('hotelPreparation.missing')}</Typography>
      ) : (
        <Alert severity="error">{t('hotelCatalogAdmin.loadError')}</Alert>
      )}
      {missing && !publication.isFetching && (
        <Button
          disabled={locked}
          onClick={() => {
            setRights(false);
            preview.mutate();
          }}
        >
          {t('hotelPreparation.preview')}
        </Button>
      )}
      {preview.isError && <Alert severity="error">{t('hotelPreparation.previewError')}</Alert>}
      {preview.isSuccess && preview.data && missing && (
        <Stack spacing={1}>
          <Typography>{preview.data.snapshot.display_name ?? hotelId}</Typography>
          <Typography>{preview.data.snapshot.category_code}</Typography>
          <Typography>
            {preview.data.snapshot.location?.country_code} · {preview.data.snapshot.location?.city} ·{' '}
            {preview.data.snapshot.location?.address}
          </Typography>
          <Typography>
            {preview.data.snapshot.location?.latitude} / {preview.data.snapshot.location?.longitude}
          </Typography>
          <Typography variant="caption">
            {t('hotelPreparation.observed')}: {preview.data.source_observed_at}
          </Typography>
          {preview.data.snapshot.descriptions?.map((d, i) => (
            <Typography key={i} sx={{ whiteSpace: 'pre-wrap' }}>
              {d.language} · {d.kind}: {d.text}
            </Typography>
          ))}
          <Stack direction="row" gap={1} flexWrap="wrap">
            {preview.data.snapshot.images?.map(
              (image, index) =>
                safeHotelImageUrl(image.url) && (
                  <img
                    key={index}
                    src={image.url}
                    alt={t('hotelEditorial.image', { index: index + 1 })}
                    width={120}
                    height={80}
                    loading="lazy"
                    referrerPolicy="no-referrer"
                  />
                ),
            )}
          </Stack>
          <FormControlLabel
            control={<Checkbox checked={rights} disabled={locked} onChange={(_, value) => setRights(value)} />}
            label={t('hotelPreparation.rights')}
          />
          <TextField
            label={t('hotelPreparation.publishReason')}
            value={publishReason}
            disabled={locked}
            onChange={(e) => setPublishReason(e.target.value)}
            inputProps={{ maxLength: 512 }}
            sx={{ '& input': { fontSize: 16 } }}
          />
          <Button
            disabled={locked || publication.isFetching || !rights || !validPriorityReason(publishReason)}
            onClick={() => {
              if (inFlight.current || locked) return;
              inFlight.current = true;
              publish.mutate();
            }}
          >
            {t('hotelPreparation.publish')}
          </Button>
        </Stack>
      )}
      {publish.isSuccess && <Alert severity="success">{t('hotelPreparation.published')}</Alert>}
      <Typography variant="h6">{t('hotelPreparation.commercial')}</Typography>
      {eligibility.isFetching ? (
        <Typography>{t('hotelCatalogAdmin.loading')}</Typography>
      ) : eligibility.isError ? (
        <Alert severity="warning">{t('hotelPreparation.statusError')}</Alert>
      ) : (
        <Typography>
          {t('hotelPreparation.agencyStatus')}: {eligibility.data?.status} · {t('hotelPreparation.links')}:{' '}
          {eligibility.data?.active_supplier_links_count}
        </Typography>
      )}
      <Typography>{t('hotelPreparation.notAvailability')}</Typography>
      {canActivate && !eligibility.isError && (
        <Stack spacing={1}>
          <FormControlLabel
            control={
              <Checkbox
                checked={activationConfirmed}
                disabled={locked}
                onChange={(_, value) => setActivationConfirmed(value)}
              />
            }
            label={t(reactivating ? 'hotelPreparation.confirmReactivation' : 'hotelPreparation.confirmActivation')}
          />
          <TextField
            label={t(reactivating ? 'hotelPreparation.reactivationReason' : 'hotelPreparation.activationReason')}
            value={activationReason}
            disabled={locked}
            onChange={(e) => setActivationReason(e.target.value)}
            inputProps={{ maxLength: 512 }}
            sx={{ '& input': { fontSize: 16 } }}
          />
          <Button
            disabled={
              locked || eligibility.isFetching || !activationConfirmed || !validPriorityReason(activationReason)
            }
            onClick={() => {
              if (inFlight.current || locked) return;
              inFlight.current = true;
              activate.mutate();
            }}
          >
            {t(reactivating ? 'hotelPreparation.reactivate' : 'hotelOnboarding.activateAgency')}
          </Button>
        </Stack>
      )}
      {uncertain && <Alert severity="warning">{t('hotelPreparation.uncertain')}</Alert>}
      <Button
        disabled={pending}
        onClick={() => {
          preview.reset();
          publish.reset();
          activate.reset();
          setRights(false);
          setActivationConfirmed(false);
          publication.refetch();
          eligibility.refetch();
          cache.invalidateQueries([...catalogAdminKey, 'detail', hotelId]);
          cache.invalidateQueries([...catalogAdminKey, 'inventory']);
        }}
      >
        {t('hotelCatalogAdmin.reload')}
      </Button>
    </Stack>
  );
}
